import axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError } from '../../utils';
import './DistributeScreen.css';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true, error: '' };
    case 'FETCH_SUCCESS':
      return {
        ...state,
        loading: false,
        error: '',
        data: action.payload,
      };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };
    default:
      return state;
  }
};

const fmtNum = (value, digits = 0) =>
  Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

const fmtDateTime = (value) => {
  if (!value) return 'Not saved';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not saved';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const geoLabel = (log) => {
  const geo = log?.geo || {};
  const parts = [geo.city, geo.region, geo.country].filter(Boolean);
  return parts.length ? parts.join(', ') : 'Unknown location';
};

const isCurrentGlobalMonth = (global) => {
  const now = new Date();
  return Number(global?.year) === now.getFullYear() && Number(global?.monthN) === now.getMonth() + 1;
};

const statusInfo = (global) => {
  if (global && !isCurrentGlobalMonth(global)) {
    return { label: 'Paid', icon: 'fa-check-circle', bg: '#d1e7dd', color: '#0f5132' };
  }
  const status = global?.workflowStatus || (global?.status ? 'distributed' : 'draft');
  if (status === 'distributed' || global?.status) {
    return { label: 'Paid', icon: 'fa-check-circle', bg: '#d1e7dd', color: '#0f5132' };
  }
  if (status === 'calculated') {
    return { label: 'Ready to Pay', icon: 'fa-calculator', bg: '#dbeafe', color: '#1d4ed8' };
  }
  if (status === 'cancelled') {
    return { label: 'Cancelled', icon: 'fa-ban', bg: '#f8d7da', color: '#842029' };
  }
  return { label: 'Draft', icon: 'fa-pencil', bg: '#fff3cd', color: '#664d03' };
};

const historyStatusInfo = (status) => {
  if (status === 'Pending') return {
    label: 'Payable', bg: '#d1e7dd', color: '#0f5132',
    help: 'Payable — this month has been paid, so the amount is now sitting in this user\'s balance. They can request a withdrawal for it.',
  };
  if (status === 'Calculated') return {
    label: 'Review', bg: '#dbeafe', color: '#1d4ed8',
    help: 'Review — this user qualified in the last list refresh, but the month is NOT paid yet, so no money has moved. It is a draft line for you to check. It becomes Payable when you press Pay Global Sales.',
  };
  if (status === 'withdraw') return {
    label: 'Withdraw Request', bg: '#fff3cd', color: '#664d03',
    help: 'Withdraw Request — the user has asked to withdraw this amount and is waiting for it to be sent. Recalculating will not change this row.',
  };
  if (status === 'withdraw_done') return {
    label: 'Paid Out', bg: '#ede9fe', color: '#5b21b6',
    help: 'Paid Out — the withdrawal was completed and the money has left. This row is final.',
  };
  if (status === 'Excluded') return {
    label: 'Excluded', bg: '#f8d7da', color: '#842029',
    help: 'Excluded — this user gets 0 and is not paid. Matches are NOT the reason: a user can sit here with far more matches than required. It happens when (1) an admin removed them by hand, (2) they were promoted into this designation this month, so they cannot earn at a rank they just reached, or (3) the last Calculate did not find them. Important: once excluded, Calculate will never put them back automatically — use Edit and tick Included to undo it.',
  };
  return { label: status || 'New', bg: '#f3f4f6', color: '#374151', help: 'This row has not been calculated yet.' };
};

// Hover help for the Qualified User Ledger headers.
const LEDGER_HELP = {
  username: 'The account that qualified for this month\'s global sales.',
  designation: 'The rank this user qualified at. Each designation has its own pool and its own required match count.',
  matches: 'Matches this user earned this month, out of the number their designation requires. They only qualify once they reach the required number. This is the qualifying test — it is not a payment amount and it does not change how much they get.',
  amount: 'What this user is due. Each designation\'s pool is split EVENLY between everyone included in it — so matches above the required number do not earn more. Adding or removing one person changes the amount for everyone else in that same designation.',
  status: 'Where this row is in the flow: Review (checked, not paid yet) -> Payable (month paid, money in balance) -> Withdraw Request -> Paid Out. Excluded means they no longer qualify and get nothing.',
  source: 'How this row got here. Auto = found by Refresh List from the match data. Manual = an admin added this user by hand, and Refresh List will not remove them.',
  control: 'Edit the amount or note, or remove the user from this month.',
};

export default function DistributeScreen() {
  const { id: globalId } = useParams();
  const { state } = useContext(Store);
  const { userInfo } = state;
  const [{ loading, error, data }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
    data: null,
  });

  const [busy, setBusy] = useState('');
  const [refreshPhase, setRefreshPhase] = useState('');
  const [settingsForm, setSettingsForm] = useState(null);
  const [showRateSettings, setShowRateSettings] = useState(false);
  const [manualForm, setManualForm] = useState({ username: '', designation: '', adminNote: '' });
  const [manualReview, setManualReview] = useState(null);
  const [manualAgreement, setManualAgreement] = useState(false);
  const [modalRow, setModalRow] = useState(null);
  const [historyEdit, setHistoryEdit] = useState(null);
  const [removeTarget, setRemoveTarget] = useState(null);
  // Excluded rows are hidden by default to keep the payable list clean. They are
  // never dropped silently — the count stays on screen so a wrong exclusion is
  // always one click from view.
  const [showExcluded, setShowExcluded] = useState(false);
  const autoRefreshRef = useRef('');

  const headers = useMemo(() => ({
    Authorization: `Bearer ${userInfo.token}`,
  }), [userInfo.token]);

  const fetchData = useCallback(async ({ preserveData = false } = {}) => {
    try {
      if (!preserveData) dispatch({ type: 'FETCH_REQUEST' });
      const { data: response } = await axios.get(
        api + `/api/costs/admin/global/distribute/${globalId}?mode=saved`,
        { headers }
      );

      dispatch({ type: 'FETCH_SUCCESS', payload: response });
      setSettingsForm({
        pointRate: String(response.global?.pointRate || response.settings?.pointRate || 59.75),
      });
      const firstDesignation = response.rows?.[0]?.designation;
      setManualForm((form) => ({ ...form, designation: form.designation || String(firstDesignation || '') }));
    } catch (err) {
      if (preserveData) {
        toast.error(getError(err));
      } else {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    }
  }, [globalId, headers]);

  const refreshList = useCallback(async ({ automatic = false } = {}) => {
    try {
      setBusy('refresh-list');
      setRefreshPhase('users');
      await axios.post(
        api + `/api/costs/admin/global/${globalId}/refresh-users`,
        {},
        { headers }
      );

      setRefreshPhase('calculate');
      const { data: response } = await axios.post(
        api + `/api/costs/admin/global/${globalId}/calculate`,
        {},
        { headers }
      );

      setRefreshPhase('results');
      await fetchData({ preserveData: true });
      if (!automatic) toast.success(response.message || 'Qualified user list refreshed.');
    } catch (err) {
      toast.error(getError(err));
      setRefreshPhase('results');
      await fetchData({ preserveData: true });
    } finally {
      setRefreshPhase('');
      setBusy('');
    }
  }, [fetchData, globalId, headers]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const savedGlobal = data?.global;
    if (!savedGlobal || autoRefreshRef.current === globalId) return;

    autoRefreshRef.current = globalId;
    const isFinal = Boolean(
      savedGlobal.status
      || savedGlobal.workflowStatus === 'distributed'
      || !isCurrentGlobalMonth(savedGlobal)
      || savedGlobal.isLocked
    );
    if (!isFinal) refreshList({ automatic: true });
  }, [data, globalId, refreshList]);

  const global = data?.global || {};
  const rows = data?.rows || [];
  const histories = data?.histories || [];
  const excludedCount = histories.filter((history) => history.isIncluded === false).length;
  const visibleHistories = showExcluded
    ? histories
    : histories.filter((history) => history.isIncluded !== false);
  const logs = data?.logs || [];
  const summary = data?.summary || {};
  const meta = statusInfo(global);
  const isPaid = global.status || global.workflowStatus === 'distributed';
  const isClosedMonth = !isCurrentGlobalMonth(global);
  const isPaidView = Boolean(isPaid || isClosedMonth);
  const isLocked = Boolean(global.isLocked);
  const isRefreshing = busy === 'refresh-list';
  const joinedUsers = Number(summary.totalUsers ?? global.users ?? 0);

  const runPost = async (url, label, body = {}) => {
    try {
      setBusy(label);
      const { data: response } = await axios.post(api + url, body, { headers });
      toast.success(response.message || 'Done');
      fetchData();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();
    try {
      setBusy('settings');
      const payload = {
        pointRate: Number(settingsForm.pointRate || 0),
      };
      const { data: response } = await axios.put(api + `/api/costs/admin/global/${globalId}`, payload, { headers });
      toast.success(response.message);
      setShowRateSettings(false);
      fetchData();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  const payNow = async () => {
    if (!window.confirm(`Pay global sales for ${global.month} ${global.year}? After this, users can request withdrawal.`)) return;
    runPost(`/api/costs/admin/global/distributenow/${globalId}`, 'pay');
  };

  const reviewManualUser = async (e) => {
    e.preventDefault();
    if (!manualForm.username.trim()) {
      toast.error('Please enter a username first.');
      return;
    }
    try {
      setBusy('review-user');
      const params = new URLSearchParams({
        username: manualForm.username.trim(),
        designation: String(manualForm.designation || ''),
      });
      const { data: response } = await axios.get(
        api + `/api/costs/admin/global/${globalId}/review-user?${params.toString()}`,
        { headers }
      );
      setManualReview(response);
      setManualAgreement(false);
      toast.success('User history loaded for review.');
    } catch (err) {
      setManualReview(null);
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  const addManualWinner = async () => {
    if (!manualReview) {
      toast.error('Please review the user before adding.');
      return;
    }
    const reason = manualForm.adminNote.trim();
    if (!reason) {
      toast.error('Please write why this user is being added.');
      return;
    }
    const impact = manualReview.impact || {};
    const ok = window.confirm(
      `Add ${manualReview.user?.username} to ${manualReview.designation?.name}? ` +
      `Qualified user count will change from ${impact.beforeWinnerCount || 0} to ${impact.afterWinnerCount || 0}.`
    );
    if (!ok) return;

    try {
      setBusy('manual');
      const { data: response } = await axios.post(
        api + `/api/costs/admin/global/${globalId}/manual-achiever`,
        {
          username: manualReview.user?.username || manualForm.username.trim(),
          designation: Number(manualForm.designation),
          adminNote: reason,
        },
        { headers }
      );
      toast.success(response.message);
      setManualForm({ username: '', designation: manualForm.designation, adminNote: '' });
      setManualReview(null);
      setManualAgreement(false);
      fetchData();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  const openRemoveUser = async (history) => {
    const row = rows.find((item) => Number(item.designation) === Number(history.designation));
    const beforeWinnerCount = row?.achieverCount || 0;
    const afterWinnerCount = Math.max(beforeWinnerCount - 1, 0);
    const beforeEach = row?.perHead || 0;
    const afterEach = afterWinnerCount > 0 ? (row?.totalPoint || 0) / afterWinnerCount : 0;
    const baseTarget = {
      history,
      comment: '',
      agreement: false,
      loadingReview: true,
      reviewError: '',
      review: null,
      impact: {
        designationName: row?.name || history.designationName || `Designation ${history.designation}`,
        beforeWinnerCount,
        afterWinnerCount,
        beforeEach,
        afterEach,
        changeEach: afterEach - beforeEach,
      },
    };
    setRemoveTarget(baseTarget);

    try {
      const params = new URLSearchParams({
        username: history.username,
        designation: String(history.designation || ''),
      });
      const { data: response } = await axios.get(
        api + `/api/costs/admin/global/${globalId}/review-user?${params.toString()}`,
        { headers }
      );
      setRemoveTarget((target) => (
        target?.history?._id === history._id
          ? { ...target, loadingReview: false, review: response }
          : target
      ));
    } catch (err) {
      setRemoveTarget((target) => (
        target?.history?._id === history._id
          ? { ...target, loadingReview: false, reviewError: getError(err) }
          : target
      ));
    }
  };

  const removeWinner = async (e) => {
    e.preventDefault();
    if (!removeTarget) return;
    const comment = removeTarget.comment.trim();
    if (!comment) {
      toast.error('Please write a reason before removing this user.');
      return;
    }
    if (!removeTarget.agreement) {
      toast.error('Please agree to the amount change before removing.');
      return;
    }
    const ok = window.confirm(`Remove ${removeTarget.history.username} from this global month?`);
    if (!ok) return;

    try {
      setBusy('remove-user');
      const { data: response } = await axios.put(
        api + `/api/costs/admin/global/history/${removeTarget.history._id}/remove`,
        { comment },
        { headers }
      );
      toast.success(response.message);
      setRemoveTarget(null);
      fetchData();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  const saveHistoryEdit = async (e) => {
    e.preventDefault();
    try {
      setBusy('history');
      // A paid month only accepts note changes — the backend rejects the whole
      // save if amount or isIncluded is present, even unchanged, so leave them out.
      const body = isPaidView
        ? { adminNote: historyEdit.adminNote }
        : {
          amount: Number(historyEdit.amount || 0),
          isIncluded: historyEdit.isIncluded,
          adminNote: historyEdit.adminNote,
        };
      const { data: response } = await axios.put(
        api + `/api/costs/admin/global/history/${historyEdit._id}`,
        body,
        { headers }
      );
      toast.success(response.message);
      setHistoryEdit(null);
      fetchData();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setBusy('');
    }
  };

  if (loading) {
    return (
      <div style={s.page}>
        <Helmet><title>Loading Global Sales</title></Helmet>
        <State icon="fa-spinner fa-spin" title="Loading global sales..." />
      </div>
    );
  }

  if (error) {
    return (
      <div style={s.page}>
        <Helmet><title>Global Sales Error</title></Helmet>
        <Link to="/admin/global" style={s.backLink}><i className="fa fa-arrow-left" /> Back to Global Sales</Link>
        <State icon="fa-exclamation-triangle" title="Could not load this month" text={error} danger />
      </div>
    );
  }

  return (
    <div style={s.page}>
      <Helmet><title>{`Global Sales - ${global.month || ''} ${global.year || ''}`}</title></Helmet>

      <Link to="/admin/global" style={s.backLink}><i className="fa fa-arrow-left" /> Back to Global Sales</Link>

      <div style={s.header}>
        <div>
          <p style={s.eyebrow}>Monthly Payment Desk</p>
          <h4 style={s.title}>{global.month} {global.year}</h4>
          <p style={s.subtitle}>
            {fmtNum(global.users)} users - {data?.matchingCount === null ? 'saved rows loaded quickly' : `${fmtNum(data?.matchingCount)} matching rows checked`}
          </p>
        </div>
        <div style={s.headerActions}>
          <Badge meta={meta} />
          {isLocked && <span style={s.lockPill}><i className="fa fa-lock" /> Locked</span>}
        </div>
      </div>

      <div style={s.actionBar}>
        <button
          type="button"
          style={s.secondaryBtn}
          disabled={isPaidView || isLocked || Boolean(busy)}
          onClick={() => refreshList()}
        >
          <i className={`fa ${isRefreshing ? 'fa-spinner fa-spin' : 'fa-refresh'}`} />
          Refresh List
        </button>
        <button
          type="button"
          style={s.primaryBtn}
          disabled={isPaidView || Boolean(busy)}
          onClick={payNow}
        >
          <i className={`fa ${busy === 'pay' ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`} />
          Pay Global Sales
        </button>
        <button
          type="button"
          style={s.secondaryBtn}
          disabled={Boolean(busy) || isPaidView}
          onClick={() => runPost(`/api/costs/admin/global/${globalId}/lock`, 'lock', { isLocked: !isLocked })}
        >
          <i className={`fa ${isLocked ? 'fa-unlock' : 'fa-lock'}`} />
          {isLocked ? 'Unlock' : 'Lock'}
        </button>
        <button
          type="button"
          style={s.secondaryBtn}
          disabled={isPaidView || !settingsForm || Boolean(busy)}
          onClick={() => setShowRateSettings(true)}
        >
          <i className="fa fa-percent" />
          Change Month Rate
        </button>
      </div>

      {isRefreshing && <RefreshSkeleton phase={refreshPhase} />}

      <div style={s.statsGrid} className={isRefreshing ? 'global-refresh-previous' : ''}>
        <Stat icon="fa-diamond" label="Global Amount" value={fmtNum(summary.totalPool, 2)} color="#7c3aed" />
        <Stat
          icon="fa-trophy"
          label="Qualified / Joined Users"
          value={`${fmtNum(summary.totalAchievers)} / ${fmtNum(joinedUsers)}`}
          color="#198754"
        />
        <Stat icon="fa-money" label="Ready Amount" value={fmtNum(summary.totalCalculated, 2)} color="#0d6efd" />
        <Stat icon="fa-check-circle" label="Paid Amount" value={fmtNum(global.totalDistributed || summary.totalDistributed, 2)} color="#f59e0b" />
      </div>

      <div style={s.panel} className={isRefreshing ? 'global-refresh-previous' : ''}>
        <div style={s.panelHeader}>
          <h6 style={s.panelTitle}>Designation Breakdown</h6>
          <span style={s.helpPill}>{rows.length} designations</span>
        </div>
        <div style={s.tableWrap}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Designation</th>
                <th style={s.th}>Active Users</th>
                <th style={s.th}>Required Matches</th>
                <th style={s.th}>Point Rate</th>
                <th style={s.th}>Designation Amount</th>
                <th style={s.th}>Qualified Users</th>
                <th style={s.th}>Each User Gets</th>
                <th style={s.th}>View</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.designation} style={s.tr}>
                  <td style={s.td}><strong>{row.name}</strong></td>
                  <td style={s.td}>{fmtNum(row.totalUsers)}</td>
                  <td style={s.td}>{fmtNum(row.requiredMatch)} matches</td>
                  <td style={s.td}>{fmtNum(row.pointRate, 2)}</td>
                  <td style={s.td}>{fmtNum(row.totalPoint, 2)}</td>
                  <td style={s.td}>{fmtNum(row.achieverCount)}</td>
                  <td style={s.td}><strong>{fmtNum(row.perHead, 2)}</strong></td>
                  <td style={s.td}>
                    <button type="button" style={s.actionBtn} onClick={() => setModalRow(row)} disabled={!row.achievers?.length}>
                      <i className="fa fa-eye" />
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {!isPaidView && (
        <div style={s.panel}>
          <div style={s.panelHeader}>
            <h6 style={s.panelTitle}>Add User Manually</h6>
            <span style={s.helpPill}>Review first, then confirm</span>
          </div>
          <form onSubmit={reviewManualUser}>
            <div style={s.infoBox}>
              <i className="fa fa-info-circle" />
              <span>Adding or removing a user changes the amount for everyone in that same designation. Press Refresh List again before payment if you want to double-check the final list.</span>
            </div>
            <div style={s.formGrid}>
              <label style={s.field}>Username
                <input
                  value={manualForm.username}
                  onChange={(e) => {
                    setManualForm({ ...manualForm, username: e.target.value });
                    setManualReview(null);
                    setManualAgreement(false);
                  }}
                  style={s.input}
                  placeholder="Enter exact username"
                />
              </label>
              <label style={s.field}>Designation
                <select
                  value={manualForm.designation}
                  onChange={(e) => {
                    setManualForm({ ...manualForm, designation: e.target.value });
                    setManualReview(null);
                    setManualAgreement(false);
                  }}
                  style={s.input}
                >
                  {rows.map((row) => <option key={row.designation} value={row.designation}>{row.name}</option>)}
                </select>
              </label>
            </div>
            <div style={s.formActions}>
              <button type="submit" style={s.secondaryBtn} disabled={busy === 'review-user' || isLocked}>
                <i className={`fa ${busy === 'review-user' ? 'fa-spinner fa-spin' : 'fa-search'}`} />
                Search Matching History
              </button>
            </div>
          </form>
          {manualReview && (
            <>
              <ManualReview review={manualReview} mode="add" />
              <div style={s.confirmBox}>
                <label style={{ ...s.field, gridColumn: '1 / -1' }}>Comment Before Adding
                  <textarea
                    value={manualForm.adminNote}
                    onChange={(e) => setManualForm({ ...manualForm, adminNote: e.target.value })}
                    style={{ ...s.input, minHeight: 90, paddingTop: 10, resize: 'vertical' }}
                    placeholder="Write why this user should be added after reviewing matching history."
                  />
                </label>
                <label style={s.agreeRow}>
                  <input
                    type="checkbox"
                    checked={manualAgreement}
                    onChange={(e) => setManualAgreement(e.target.checked)}
                  />
                  I reviewed the matching history and understand the amount change for this designation.
                </label>
                <div style={s.formActions}>
                  <button
                    type="button"
                    style={s.primaryBtn}
                    disabled={
                      busy === 'manual' ||
                      isLocked ||
                      manualReview?.checks?.alreadyInThisMonth ||
                      !manualForm.adminNote.trim() ||
                      !manualAgreement
                    }
                    onClick={addManualWinner}
                  >
                    <i className={`fa ${busy === 'manual' ? 'fa-spinner fa-spin' : 'fa-user-plus'}`} />
                    Add User
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      <div style={s.panel} className={isRefreshing ? 'global-refresh-previous' : ''}>
        <div style={s.panelHeader}>
          <h6 style={s.panelTitle}>Qualified User Ledger</h6>
          <div style={s.panelHeaderRight}>
            <span style={s.helpPill}>
              {visibleHistories.length} {showExcluded ? 'saved rows' : 'included rows'}
            </span>
            {excludedCount > 0 && (
              <button
                type="button"
                style={{ ...s.excludeToggle, ...(showExcluded ? s.excludeToggleOn : null) }}
                onClick={() => setShowExcluded((current) => !current)}
                title={showExcluded
                  ? 'Hide the excluded rows again. They stay in the ledger either way — they are only hidden from view.'
                  : `${excludedCount} user${excludedCount === 1 ? '' : 's'} on this month are excluded and will be paid 0. Click to see who, and why. Excluded rows are never removed — only hidden.`}
              >
                <i className={`fa ${showExcluded ? 'fa-eye-slash' : 'fa-ban'}`} />
                {showExcluded ? `Hide excluded (${excludedCount})` : `${excludedCount} excluded — show`}
              </button>
            )}
          </div>
        </div>
        {histories.length === 0 ? (
          <State icon="fa-inbox" title="No saved qualified users yet" text="Refresh the list to create review rows." />
        ) : visibleHistories.length === 0 ? (
          <State
            icon="fa-ban"
            danger
            title={`Every saved row on this month is excluded (${excludedCount})`}
            text="Nobody is set to be paid. Press the excluded button above to see who they are and why, in case this is not what you expected."
          />
        ) : (
          <>
            <div style={s.legend}>
              <i className="fa fa-info-circle" />
              <span>
                Hover any column title for a full explanation. <strong>Matches</strong> is only a pass/fail test (earned / needed) — extra matches never pay more, and a high number does <em>not</em> keep a user included.
                {' '}<strong>Status</strong> is where the row sits: <strong>Review</strong> = qualified but not paid yet, nothing has moved;
                {' '}<strong>Payable</strong> = month paid, money is in their balance; <strong>Excluded</strong> = gets 0.
                {showExcluded && ' An excluded user can still show plenty of matches — the reason is shown under the badge, and their match number is a frozen snapshot, not a live count.'}
                {' '}<strong>Source</strong>: <strong>Auto</strong> = found by Refresh List, <strong>Manual</strong> = added by an admin and protected from automatic refresh.
                {!showExcluded && excludedCount > 0 && (
                  <> <strong>{excludedCount} excluded row{excludedCount === 1 ? ' is' : 's are'} hidden</strong> and will be paid 0 — use the button above to check them.</>
                )}
              </span>
            </div>
            <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th} title={LEDGER_HELP.username}>Username</th>
                  <th style={s.th} title={LEDGER_HELP.designation}>Designation</th>
                  <th style={s.thHelp} title={LEDGER_HELP.matches}>Matches <i className="fa fa-question-circle" style={s.helpIcon} /></th>
                  <th style={s.thHelp} title={LEDGER_HELP.amount}>Amount <i className="fa fa-question-circle" style={s.helpIcon} /></th>
                  <th style={s.thHelp} title={LEDGER_HELP.status}>Status <i className="fa fa-question-circle" style={s.helpIcon} /></th>
                  <th style={s.thHelp} title={LEDGER_HELP.source}>Source <i className="fa fa-question-circle" style={s.helpIcon} /></th>
                  <th style={s.th} title={LEDGER_HELP.control}>Control</th>
                </tr>
              </thead>
              <tbody>
                {visibleHistories.map((history) => {
                  const hMeta = historyStatusInfo(history.status);
                  return (
                    <tr key={history._id} style={s.tr}>
                      <td style={s.td}><strong>{history.username}</strong></td>
                      <td style={s.td}>{history.designationName || `#${history.designation}`}</td>
                      <td
                        style={s.td}
                        title={history.isIncluded === false
                          ? `Frozen snapshot. ${history.username} had ${fmtNum(history.matchCount)} of ${fmtNum(history.requiredMatch)} matches the last time they were included. Excluded rows are NOT refreshed by Calculate, so this number can be out of date and is not the reason they are excluded — see the Status column for that.`
                          : `${history.username} earned ${fmtNum(history.matchCount)} matches this month and needs ${fmtNum(history.requiredMatch)} to qualify at ${history.designationName || `#${history.designation}`}. Extra matches above the required number do not increase the amount.`}
                      >
                        {fmtNum(history.matchCount)} / {fmtNum(history.requiredMatch)}
                        {history.isIncluded === false && <span style={s.staleTag}>stale</span>}
                      </td>
                      <td style={s.td} title={LEDGER_HELP.amount}>{fmtNum(history.amount, 2)}</td>
                      <td style={s.td} title={history.adminNote ? `${hMeta.help}\n\nReason on file: ${history.adminNote}` : hMeta.help}>
                        <Badge meta={{ ...hMeta, icon: history.isIncluded === false ? 'fa-ban' : 'fa-circle' }} />
                        {history.isIncluded === false && (
                          <div style={s.excludeReason} title={history.adminNote || ''}>
                            <i className="fa fa-info-circle" />{' '}
                            {history.adminNote || 'No reason was saved for this exclusion.'}
                          </div>
                        )}
                      </td>
                      <td
                        style={s.td}
                        title={history.source === 'manual'
                          ? 'Manual — an admin added this user by hand. Refresh List will not remove or exclude them.'
                          : 'Auto — Refresh List found this user from the match data. A later refresh can exclude them if they stop qualifying.'}
                      >
                        {history.source === 'manual' ? 'Manual' : 'Auto'}
                      </td>
                      <td style={s.td}>
                        <div style={s.rowActions}>
                          <button type="button" style={s.actionBtn} onClick={() => setHistoryEdit({
                            ...history,
                            amount: String(history.amount || 0),
                            adminNote: history.adminNote || '',
                            isIncluded: history.isIncluded !== false,
                          })}>
                            <i className="fa fa-pencil" />
                            Edit
                          </button>
                          {!isPaidView && history.isIncluded !== false && !['withdraw', 'withdraw_done'].includes(history.status) && (
                            <button type="button" style={{ ...s.actionBtn, color: '#dc3545' }} onClick={() => openRemoveUser(history)} disabled={isLocked}>
                              <i className="fa fa-user-times" />
                              Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          </>
        )}
      </div>

      <div style={s.panel}>
        <div style={s.panelHeader}>
          <h6 style={s.panelTitle}>Change Logs</h6>
          <span style={s.helpPill}>{logs.length} latest logs</span>
        </div>
        {logs.length === 0 ? (
          <State icon="fa-history" title="No changes logged yet" text="Add, remove, setting, lock, calculate, and payment actions will appear here." />
        ) : (
          <div style={s.logList}>
            {logs.map((log) => (
              <div key={log._id} style={s.logItem}>
                <div style={s.logIcon}><i className="fa fa-history" /></div>
                <div style={s.logBody}>
                  <div style={s.logTop}>
                    <strong>{log.actionLabel || log.action}</strong>
                    <span>{fmtDateTime(log.createdAt)}</span>
                  </div>
                  <div style={s.smallText}>
                    <i className="fa fa-user" /> {log.adminName || 'Admin'}
                    {log.targetUsername ? <> - <i className="fa fa-id-card" /> {log.targetUsername}</> : null}
                  </div>
                  {log.comment && <div style={s.logComment}>{log.comment}</div>}
                  {Array.isArray(log.changes) && log.changes.length > 0 && (
                    <div style={s.changeList}>
                      {log.changes.slice(0, 4).map((change, index) => (
                        <span key={`${log._id}-${change.field}-${index}`} style={s.changePill}>
                          {change.label || change.field}: {String(change.before ?? 'blank')} to {String(change.after ?? 'blank')}
                        </span>
                      ))}
                    </div>
                  )}
                  {log.impact && log.impact.beforeWinnerCount !== undefined && (
                    <div style={s.smallText}>
                      <i className="fa fa-exchange" /> Qualified users {fmtNum(log.impact.beforeWinnerCount)} to {fmtNum(log.impact.afterWinnerCount)}; each {fmtNum(log.impact.beforeEach, 2)} to {fmtNum(log.impact.afterEach, 2)}
                    </div>
                  )}
                  <div style={s.logMeta}>
                    <span><i className="fa fa-map-marker" /> {geoLabel(log)}</span>
                    <span><i className="fa fa-wifi" /> {log.ip || 'Unknown IP'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modalRow && (
        <div style={s.modalOverlay} onClick={() => setModalRow(null)}>
          <div style={s.modal} onClick={(e) => e.stopPropagation()}>
            <div style={s.panelHeader}>
              <h6 style={s.panelTitle}>{modalRow.name} Qualified Users</h6>
              <button type="button" style={s.closeBtn} onClick={() => setModalRow(null)}>&times;</button>
            </div>
            <div style={s.winnerGrid}>
              {modalRow.achievers.map((winner) => (
                <div key={winner.username} style={s.winnerItem}>
                  <div style={s.avatar}>{String(winner.username || '?')[0].toUpperCase()}</div>
                  <div>
                    <strong>{winner.username}</strong>
                    <div style={s.smallText}>{fmtNum(winner.matchCount)} matches - {fmtNum(winner.amount ?? modalRow.perHead, 2)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {showRateSettings && settingsForm && (
        <div style={s.modalOverlay} onClick={() => setShowRateSettings(false)}>
          <form style={s.modal} onSubmit={saveSettings} onClick={(e) => e.stopPropagation()}>
            <div style={s.panelHeader}>
              <h6 style={s.panelTitle}>Change Month Rate</h6>
              <button type="button" style={s.closeBtn} onClick={() => setShowRateSettings(false)}>&times;</button>
            </div>
            <div style={s.infoBox}>
              <i className="fa fa-info-circle" />
              <span>This only changes the rate for this month. Joined users and scan rules stay automatic.</span>
            </div>
            <div style={s.formGrid}>
              <label style={s.field}>Rate For This Month
                <input
                  value={settingsForm.pointRate}
                  disabled={isPaidView || isLocked}
                  onChange={(e) => setSettingsForm({ ...settingsForm, pointRate: e.target.value })}
                  style={s.input}
                  type="number"
                  min="0"
                  step="0.01"
                />
              </label>
              <div style={s.rateHint}>
                <span>Default Rate</span>
                <strong>{fmtNum(data?.settings?.pointRate, 2)}</strong>
              </div>
            </div>
            {isLocked && (
              <div style={{ ...s.warningBox, marginTop: 14 }}>
                <i className="fa fa-lock" />
                <span>Unlock this month before changing the rate.</span>
              </div>
            )}
            <div style={s.formActions}>
              <button type="button" style={s.secondaryBtn} onClick={() => setShowRateSettings(false)}>Cancel</button>
              <button type="submit" style={s.primaryBtn} disabled={busy === 'settings' || isPaidView || isLocked}>
                <i className={`fa ${busy === 'settings' ? 'fa-spinner fa-spin' : 'fa-save'}`} />
                Save Rate
              </button>
            </div>
          </form>
        </div>
      )}

      {removeTarget && (
        <div style={s.modalOverlay} onClick={() => setRemoveTarget(null)}>
          <form style={s.modal} onSubmit={removeWinner} onClick={(e) => e.stopPropagation()}>
            <div style={s.panelHeader}>
              <h6 style={s.panelTitle}>Remove {removeTarget.history.username}</h6>
              <button type="button" style={s.closeBtn} onClick={() => setRemoveTarget(null)}>&times;</button>
            </div>
            {removeTarget.loadingReview ? (
              <State icon="fa-spinner fa-spin" title="Loading matching history..." />
            ) : removeTarget.reviewError ? (
              <State icon="fa-exclamation-triangle" title="Could not load matching history" text={removeTarget.reviewError} danger />
            ) : (
              <div style={{ marginBottom: 14 }}>
                <MatchingHistoryGroups groups={removeTarget.review?.history?.matchingByMonth || []} />
              </div>
            )}
            <div style={s.warningBox}>
              <i className="fa fa-warning" />
              <span>
                This removes the user from {removeTarget.impact.designationName}. Qualified user count changes from {fmtNum(removeTarget.impact.beforeWinnerCount)} to {fmtNum(removeTarget.impact.afterWinnerCount)}. The remaining users in this designation may receive {fmtNum(removeTarget.impact.afterEach, 2)} each instead of {fmtNum(removeTarget.impact.beforeEach, 2)}.
              </span>
            </div>
            <label style={{ ...s.field, marginTop: 14 }}>Reason for Removing
              <textarea
                value={removeTarget.comment}
                onChange={(e) => setRemoveTarget({ ...removeTarget, comment: e.target.value })}
                style={{ ...s.input, minHeight: 96, paddingTop: 10, resize: 'vertical' }}
                placeholder="Write the reason. This will be saved in the log."
              />
            </label>
            <label style={s.agreeRow}>
              <input
                type="checkbox"
                checked={Boolean(removeTarget.agreement)}
                onChange={(e) => setRemoveTarget({ ...removeTarget, agreement: e.target.checked })}
              />
              I reviewed the matching history and understand the amount change for remaining users.
            </label>
            <div style={s.formActions}>
              <button type="button" style={s.secondaryBtn} onClick={() => setRemoveTarget(null)}>Cancel</button>
              <button
                type="submit"
                style={{ ...s.primaryBtn, background: '#dc3545' }}
                disabled={busy === 'remove-user' || removeTarget.loadingReview || Boolean(removeTarget.reviewError) || !removeTarget.comment.trim() || !removeTarget.agreement}
              >
                <i className={`fa ${busy === 'remove-user' ? 'fa-spinner fa-spin' : 'fa-user-times'}`} />
                Remove User
              </button>
            </div>
          </form>
        </div>
      )}

      {historyEdit && (
        <div style={s.modalOverlay} onClick={() => setHistoryEdit(null)}>
          <form style={s.modal} onSubmit={saveHistoryEdit} onClick={(e) => e.stopPropagation()}>
            <div style={s.panelHeader}>
              <h6 style={s.panelTitle}>Edit {historyEdit.username}</h6>
              <button type="button" style={s.closeBtn} onClick={() => setHistoryEdit(null)}>&times;</button>
            </div>
            <div style={s.formGrid}>
              <label style={s.field}>Amount
                <input value={historyEdit.amount} disabled={isPaidView} onChange={(e) => setHistoryEdit({ ...historyEdit, amount: e.target.value })} style={s.input} type="number" min="0" step="0.01" />
              </label>
              <label style={{ ...s.field, justifyContent: 'flex-end' }}>
                <span style={s.checkboxRow}>
                  <input checked={historyEdit.isIncluded} disabled={isPaidView} onChange={(e) => setHistoryEdit({ ...historyEdit, isIncluded: e.target.checked })} type="checkbox" />
                  Include in payment
                </span>
              </label>
              <label style={{ ...s.field, gridColumn: '1 / -1' }}>Note
                <textarea value={historyEdit.adminNote} onChange={(e) => setHistoryEdit({ ...historyEdit, adminNote: e.target.value })} style={{ ...s.input, minHeight: 90, paddingTop: 10, resize: 'vertical' }} />
              </label>
            </div>
            <div style={s.formActions}>
              <button type="button" style={s.secondaryBtn} onClick={() => setHistoryEdit(null)}>Cancel</button>
              <button type="submit" style={s.primaryBtn} disabled={busy === 'history'}>
                <i className={`fa ${busy === 'history' ? 'fa-spinner fa-spin' : 'fa-save'}`} />
                Save User
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function RefreshSkeleton({ phase }) {
  const messages = {
    users: 'Checking joined users...',
    calculate: 'Calculating qualified users...',
    results: 'Loading the refreshed list...',
  };

  return (
    <div className="global-refresh-skeleton" role="status" aria-live="polite">
      <div className="global-refresh-skeleton__status">
        <i className="fa fa-refresh fa-spin" />
        <span>{messages[phase] || 'Refreshing the list...'}</span>
      </div>
      <div className="global-refresh-skeleton__grid" aria-hidden="true">
        <span className="global-refresh-skeleton__card" />
        <span className="global-refresh-skeleton__card" />
        <span className="global-refresh-skeleton__card" />
        <span className="global-refresh-skeleton__card" />
      </div>
    </div>
  );
}

function Stat({ icon, label, value, color }) {
  return (
    <div style={s.stat}>
      <div style={s.statIcon(color)}><i className={`fa ${icon}`} /></div>
      <div>
        <div style={s.statLabel}>{label}</div>
        <div style={s.statValue}>{value}</div>
      </div>
    </div>
  );
}

function Badge({ meta }) {
  return (
    <span style={{ ...s.badge, background: meta.bg, color: meta.color }}>
      <i className={`fa ${meta.icon}`} />
      {meta.label}
    </span>
  );
}

function State({ icon, title, text, danger }) {
  return (
    <div style={s.emptyState}>
      <i className={`fa ${icon}`} style={{ color: danger ? '#dc3545' : '#0d6efd', fontSize: 30 }} />
      <h6 style={{ margin: '14px 0 4px', color: danger ? '#dc3545' : '#111827' }}>{title}</h6>
      {text && <p style={{ margin: 0, color: '#6b7280' }}>{text}</p>}
    </div>
  );
}

function ManualReview({ review, mode = 'add' }) {
  const impact = review.impact || {};
  const checks = review.checks || {};
  const user = review.user || {};
  const designation = review.designation || {};
  const history = review.history || {};
  const previousGlobal = history.previousGlobalHistory || [];
  const promotions = history.promotionHistory || [];
  const matchingGroups = history.matchingByMonth || [];
  const actionWord = mode === 'remove' ? 'Remove from' : 'Add to';

  return (
    <div style={s.reviewBox}>
      <div style={s.reviewHeader}>
        <div style={s.avatar}>{String(user.username || '?')[0].toUpperCase()}</div>
        <div>
          <h6 style={{ margin: 0, fontWeight: 900 }}>{user.name || user.username}</h6>
          <div style={s.smallText}>
            Username: {user.username} - Current designation: {user.designation || 0}
            {review.links?.userReport && (
              <> - <Link to={review.links.userReport} style={s.inlineLink}>Open full user report</Link></>
            )}
          </div>
        </div>
      </div>

      <div style={s.reviewGrid}>
        <ReviewStat icon="fa-check-circle" label="Account" value={checks.userActive ? 'Active' : 'Blocked or inactive'} danger={!checks.userActive} />
        <ReviewStat icon="fa-handshake-o" label="Month Matches" value={`${fmtNum(checks.monthlyMatchCount)} / ${fmtNum(designation.requiredMatch)} needed`} />
        <ReviewStat icon="fa-trophy" label="Promoted This Month" value={checks.promotedThisMonth ? 'Yes' : 'No'} danger={checks.promotedThisMonth} />
        <ReviewStat icon="fa-list" label="Existing Row" value={checks.alreadyInThisMonth ? `${checks.existingStatus || 'Saved'} (${checks.existingSource || 'row'})` : 'Not added yet'} />
      </div>

      <div style={s.impactBox}>
        <i className="fa fa-calculator" />
        <span>
          {actionWord} {impact.designationName || designation.name}. Qualified user count changes from {fmtNum(impact.beforeWinnerCount)} to {fmtNum(impact.afterWinnerCount)}. Each included user in this designation will receive {fmtNum(impact.afterEach, 2)} instead of {fmtNum(impact.beforeEach, 2)}.
        </span>
      </div>

      <MatchingHistoryGroups groups={matchingGroups} />

      <div style={s.historyGrid}>
        <MiniHistory title="Previous Global Sales" rows={previousGlobal} empty="No previous global sales rows." />
        <MiniHistory title="Promotion History" rows={promotions} empty="No promotion history found." promotion />
      </div>
    </div>
  );
}

function MatchingHistoryGroups({ groups }) {
  return (
    <div style={s.matchingBox}>
      <h6 style={s.miniTitle}>Matching History by Month ({groups.length})</h6>
      {groups.length === 0 ? (
        <p style={s.smallText}>No matching history found for this user.</p>
      ) : (
        <div style={s.matchingGroups}>
          {groups.map((group) => (
            <div key={group.key} style={s.matchingGroup}>
              <div style={s.matchingHeader}>
                <strong>{group.label}</strong>
                <span>{fmtNum(group.count)} matches - {fmtNum(group.totalAmount, 2)} points</span>
              </div>
              <div style={s.miniList}>
                {(group.rows || []).map((row) => (
                  <div key={row._id} style={s.miniRow}>
                    <strong>{fmtNum(row.amount, 2)} points</strong>
                    <span>{row.status || 'Matching'}{row.details ? ` - ${row.details}` : ''}</span>
                    <small>{fmtDateTime(row.createdAt)}</small>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ReviewStat({ icon, label, value, danger }) {
  return (
    <div style={s.reviewStat}>
      <i className={`fa ${icon}`} style={{ color: danger ? '#dc3545' : '#0d6efd' }} />
      <div>
        <div style={s.statLabel}>{label}</div>
        <strong>{value}</strong>
      </div>
    </div>
  );
}

function MiniHistory({ title, rows, empty, promotion, matching }) {
  return (
    <div style={s.miniPanel}>
      <h6 style={s.miniTitle}>{title} ({rows.length})</h6>
      {rows.length === 0 ? (
        <p style={s.smallText}>{empty}</p>
      ) : (
        <div style={s.miniList}>
          {rows.map((row) => (
            <div key={row._id} style={s.miniRow}>
              <strong>
                {promotion
                  ? row.position_name || `Designation ${row.position_serial || ''}`
                  : matching
                    ? fmtNum(row.amount, 2)
                    : fmtNum(row.amount, 2)}
              </strong>
              <span>
                {promotion
                  ? row.status || 'Saved'
                  : matching
                    ? row.status || 'Matching'
                    : `${row.designationName || `Designation ${row.designation || ''}`} - ${row.status || 'Saved'}`}
              </span>
              <small>{fmtDateTime(row.createdAt)}</small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  page: {
    padding: 20,
    maxWidth: 1240,
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
    color: '#111827',
  },
  backLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    color: '#4b5563',
    textDecoration: 'none',
    fontWeight: 800,
    marginBottom: 16,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 16,
  },
  headerActions: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', justifyContent: 'flex-end' },
  eyebrow: { margin: 0, fontSize: 12, fontWeight: 800, color: '#0d6efd', textTransform: 'uppercase' },
  title: { margin: '2px 0', fontSize: 28, fontWeight: 800 },
  subtitle: { margin: 0, color: '#6b7280', fontSize: 14 },
  lockPill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '7px 11px',
    borderRadius: 999,
    background: '#f3f4f6',
    color: '#374151',
    fontWeight: 800,
    fontSize: 12,
  },
  actionBar: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
    gap: 12,
    marginBottom: 16,
  },
  stat: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 16,
  },
  statIcon: (color) => ({
    width: 42,
    height: 42,
    borderRadius: 8,
    background: `${color}18`,
    color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
  }),
  statLabel: { color: '#6b7280', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' },
  statValue: { fontSize: 22, fontWeight: 800 },
  panel: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
  },
  panelHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
  },
  panelTitle: { margin: 0, fontSize: 16, fontWeight: 800 },
  helpPill: {
    padding: '5px 10px',
    borderRadius: 999,
    background: '#eef2ff',
    color: '#3730a3',
    fontSize: 12,
    fontWeight: 800,
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 12,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    color: '#374151',
    fontSize: 13,
    fontWeight: 800,
  },
  input: {
    minHeight: 40,
    border: '1px solid #d1d5db',
    borderRadius: 8,
    padding: '0 12px',
    fontSize: 14,
    background: '#fff',
    color: '#111827',
  },
  formActions: { display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 14 },
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 980 },
  th: {
    textAlign: 'left',
    padding: '13px 14px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
  },
  panelHeaderRight: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  excludeToggle: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    border: '1px solid #fecaca',
    background: '#fef2f2',
    color: '#842029',
    borderRadius: 999,
    padding: '5px 11px',
    fontSize: 12,
    fontWeight: 800,
    cursor: 'pointer',
  },
  excludeToggleOn: { border: '1px solid #d1d5db', background: '#f3f4f6', color: '#374151' },
  legend: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 9,
    padding: '11px 13px',
    margin: '0 0 12px',
    background: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: 10,
    color: '#0c4a6e',
    fontSize: 12.5,
    lineHeight: 1.6,
  },
  thHelp: {
    textAlign: 'left',
    padding: '13px 14px',
    background: '#f9fafb',
    borderBottom: '1px solid #e5e7eb',
    color: '#6b7280',
    fontSize: 12,
    fontWeight: 800,
    textTransform: 'uppercase',
    cursor: 'help',
    whiteSpace: 'nowrap',
  },
  helpIcon: { marginLeft: 5, opacity: 0.55, fontSize: 11 },
  excludeReason: {
    marginTop: 5,
    maxWidth: 230,
    fontSize: 11,
    lineHeight: 1.45,
    color: '#842029',
    cursor: 'help',
  },
  staleTag: {
    marginLeft: 6,
    padding: '1px 5px',
    borderRadius: 5,
    background: '#f3f4f6',
    color: '#6b7280',
    fontSize: 9.5,
    fontWeight: 800,
    textTransform: 'uppercase',
    cursor: 'help',
  },
  td: { padding: '13px 14px', borderBottom: '1px solid #f3f4f6', verticalAlign: 'middle' },
  tr: { background: '#fff' },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: 'nowrap',
  },
  primaryBtn: {
    border: 'none',
    borderRadius: 8,
    padding: '10px 14px',
    background: '#0d6efd',
    color: '#fff',
    fontWeight: 800,
    fontSize: 14,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    minHeight: 40,
  },
  secondaryBtn: {
    border: '1px solid #d1d5db',
    borderRadius: 8,
    padding: '9px 13px',
    background: '#fff',
    color: '#374151',
    fontWeight: 800,
    fontSize: 14,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    minHeight: 40,
  },
  actionBtn: {
    minHeight: 34,
    borderRadius: 8,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '7px 10px',
    fontSize: 12,
    fontWeight: 800,
    cursor: 'pointer',
    textDecoration: 'none',
  },
  rowActions: { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  infoBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: 12,
    borderRadius: 8,
    background: '#eff6ff',
    color: '#1d4ed8',
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 14,
  },
  warningBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: 12,
    borderRadius: 8,
    background: '#fff3cd',
    color: '#664d03',
    fontSize: 13,
    fontWeight: 800,
  },
  rateHint: {
    minHeight: 40,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: '8px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
    background: '#f9fafb',
    color: '#374151',
    fontSize: 13,
    fontWeight: 800,
  },
  emptyState: { textAlign: 'center', padding: '55px 20px' },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(17, 24, 39, 0.45)',
    zIndex: 9999,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
  },
  modal: {
    width: '100%',
    maxWidth: 720,
    maxHeight: '82vh',
    overflowY: 'auto',
    background: '#fff',
    borderRadius: 8,
    padding: 18,
    boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
  },
  closeBtn: {
    border: 'none',
    background: '#f3f4f6',
    color: '#374151',
    width: 34,
    height: 34,
    borderRadius: 8,
    fontSize: 22,
    cursor: 'pointer',
    lineHeight: 1,
  },
  winnerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))',
    gap: 10,
  },
  winnerItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 10,
  },
  avatar: {
    width: 34,
    height: 34,
    borderRadius: 999,
    background: '#eff6ff',
    color: '#1d4ed8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
  },
  smallText: { color: '#6b7280', fontSize: 12, marginTop: 2 },
  checkboxRow: { display: 'inline-flex', alignItems: 'center', gap: 8, minHeight: 40 },
  reviewBox: {
    border: '1px solid #dbeafe',
    borderRadius: 8,
    padding: 14,
    marginTop: 16,
    background: '#fbfdff',
  },
  reviewHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 },
  inlineLink: { color: '#0d6efd', fontWeight: 800, textDecoration: 'none' },
  reviewGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 10,
    marginBottom: 12,
  },
  reviewStat: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 10,
    background: '#fff',
  },
  impactBox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    borderRadius: 8,
    padding: 12,
    background: '#ecfdf5',
    color: '#065f46',
    fontSize: 13,
    fontWeight: 800,
    marginBottom: 12,
  },
  historyGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(230px, 1fr))',
    gap: 10,
    marginTop: 12,
  },
  confirmBox: {
    borderTop: '1px solid #e5e7eb',
    marginTop: 14,
    paddingTop: 14,
  },
  agreeRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 8,
    color: '#374151',
    fontSize: 13,
    fontWeight: 800,
    marginTop: 12,
  },
  matchingBox: {
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    background: '#fff',
    marginBottom: 12,
  },
  matchingGroups: { display: 'grid', gap: 10 },
  matchingGroup: {
    border: '1px solid #f3f4f6',
    borderRadius: 8,
    padding: 10,
    background: '#fafafa',
  },
  matchingHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 10,
    flexWrap: 'wrap',
    marginBottom: 8,
    color: '#111827',
    fontSize: 13,
  },
  miniPanel: { border: '1px solid #e5e7eb', borderRadius: 8, padding: 12, background: '#fff' },
  miniTitle: { margin: '0 0 8px', fontSize: 13, fontWeight: 900, color: '#111827' },
  miniList: { display: 'grid', gap: 8 },
  miniRow: {
    display: 'grid',
    gap: 2,
    paddingBottom: 8,
    borderBottom: '1px solid #f3f4f6',
    fontSize: 12,
    color: '#374151',
  },
  logList: { display: 'grid', gap: 10 },
  logItem: {
    display: 'flex',
    gap: 10,
    border: '1px solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    background: '#fff',
  },
  logIcon: {
    width: 34,
    height: 34,
    borderRadius: 8,
    background: '#f3f4f6',
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
  },
  logBody: { flex: 1, minWidth: 0 },
  logTop: { display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', fontSize: 13 },
  logComment: {
    marginTop: 6,
    color: '#111827',
    fontSize: 13,
    fontWeight: 700,
    overflowWrap: 'anywhere',
  },
  changeList: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 },
  changePill: {
    display: 'inline-flex',
    borderRadius: 999,
    padding: '4px 8px',
    background: '#f3f4f6',
    color: '#374151',
    fontSize: 11,
    fontWeight: 800,
  },
  logMeta: { display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 8, color: '#6b7280', fontSize: 12 },
};
